import { useEffect, useRef } from 'react';

// Google Identity Services client ID (public). Blank disables Google sign-in.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';
/** True when a Google client ID is configured — gate the button + divider on this. */
export const GOOGLE_ENABLED = !!GOOGLE_CLIENT_ID;

// Minimal shape of the Google Identity Services global (window.google) we use.
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: { client_id: string; callback: (r: { credential?: string }) => void }): void;
      renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
    };
  };
}

/** A thin "— or —" divider matching the auth card styling. */
export function AuthOrDivider({ label = 'or' }: { label?: string }) {
  return (
    <div
      aria-hidden="true"
      style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0', color: '#98a2b3', fontSize: 13 }}
    >
      <span style={{ flex: 1, height: 1, background: 'currentColor', opacity: 0.25 }} />
      {label}
      <span style={{ flex: 1, height: 1, background: 'currentColor', opacity: 0.25 }} />
    </div>
  );
}

/**
 * Official Google Identity Services "Sign in with Google" button. Loads the GIS
 * script once, renders the button, and hands the resulting ID token to
 * `onCredential`. Renders nothing when VITE_GOOGLE_CLIENT_ID is unset.
 *
 * The SAME button signs in AND signs up — the backend POST /api/auth/google
 * handler is find-or-create — so the Login and Register pages both use it.
 */
export function GoogleSignInButton({
  onCredential,
  text = 'continue_with',
}: {
  onCredential: (idToken: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
}) {
  const btnRef = useRef<HTMLDivElement>(null);
  // Keep the latest callback in a ref so the once-registered GIS callback always
  // invokes the current handler without re-initializing GIS on every render.
  const cbRef = useRef(onCredential);
  cbRef.current = onCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !btnRef.current) return;

    const render = () => {
      const gis = (window as unknown as { google?: GoogleIdentityServices }).google;
      if (!gis || !btnRef.current) return;
      gis.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r) => { if (r.credential) cbRef.current(r.credential); },
      });
      gis.accounts.id.renderButton(btnRef.current, {
        type: 'standard', theme: 'outline', size: 'large', text,
        shape: 'rectangular', logo_alignment: 'left', width: 300,
      });
    };

    // GIS already loaded (e.g. navigated between the two auth pages) → render now.
    if ((window as unknown as { google?: GoogleIdentityServices }).google) {
      render();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
    // Keep the GIS script loaded for the other auth page; don't remove on unmount.
  }, [text]);

  if (!GOOGLE_CLIENT_ID) return null;
  return <div ref={btnRef} style={{ display: 'flex', justifyContent: 'center' }} />;
}
